import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Header from '../../components/Header';
import { api } from '../../helpers/apiUtils';

/**
 * Send Pauses CRUD UI for `adserver_send_pauses`.
 *
 * Replaces the FTWP-only pause editor — once this is in active use the FTWP
 * equivalent should be retired so there's a single source of truth.
 */

interface PauseRow {
  id: number;
  customer: string;
  contact_person: string;
  date: string; // YYYY-MM-DD
  material_type: 'sam' | 'nl_ad' | 'push_ad' | 'stop_nl_send';
  /**
   * Comma-separated client codes (e.g. 'ft' or 'ft,bg,sw'). NULL = global
   * pause (blocks every pipeline of the matching material_type). See
   * `helpers/adserver/adServerSendPauseChecks.getSendPauseForDate`.
   */
  client_codes: string | null;
  added_at: string;
}

const MATERIAL_TYPES: PauseRow['material_type'][] = ['sam', 'nl_ad', 'push_ad', 'stop_nl_send'];

// Mirror of VALID_CLIENT_CODES in `node-app/src/routes/adserver-send-pauses.ts`.
// Add KME/DNV here only if those clients gain SAM pipelines (today they're
// direct-mailing-only).
const VALID_CLIENT_CODES = ['ft', 'bg', 'be', 'sw', 'tt', 'nfa'] as const;
type ClientCode = (typeof VALID_CLIENT_CODES)[number];

const formatDate = (s: string) => {
  if (!s) return '';
  const d = new Date(s);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const AdserverSendPausesPage: React.FC = () => {
  const [rows, setRows] = useState<PauseRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [customer, setCustomer] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [date, setDate] = useState('');
  const [materialType, setMaterialType] = useState<PauseRow['material_type']>('sam');
  // Default = all 5 codes pre-selected = UX equivalent of "global pause"
  // (matches how every row was treated pre-2026-05-29). Operator unchecks to
  // scope. Empty selection on submit is allowed and stores NULL (= global).
  const [clientCodes, setClientCodes] = useState<ClientCode[]>([...VALID_CLIENT_CODES]);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/api/adserver/send-pauses');
      setRows(data?.data ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!date) {
      setError('Datum erforderlich');
      return;
    }
    setError(null);
    setInfo(null);
    setCreating(true);
    try {
      // When the operator leaves the full set selected we pass [] to the
      // backend so it stores NULL (= global pause, matches the audit-trail
      // shape of pre-2026-05-29 rows). An explicit subset survives as a
      // comma-separated list on the server.
      const fullSetSelected = clientCodes.length === VALID_CLIENT_CODES.length;
      const { data } = await api.post('/api/adserver/send-pauses', {
        customer,
        contact_person: contactPerson,
        date,
        material_type: materialType,
        client_codes: fullSetSelected ? [] : clientCodes,
      });
      if (data?.success) {
        setInfo(`Pause #${data.id} angelegt.`);
        setCustomer('');
        setContactPerson('');
        setDate('');
        setMaterialType('sam');
        setClientCodes([...VALID_CLIENT_CODES]);
        load();
      } else {
        setError(data?.error ?? 'Anlegen fehlgeschlagen');
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(`Pause #${id} löschen?`)) return;
    setError(null);
    try {
      const { data } = await api.delete(`/api/adserver/send-pauses/${id}`);
      if (data?.success) {
        setInfo(`Pause #${id} gelöscht.`);
        load();
      } else {
        setError(data?.error ?? 'Löschen fehlgeschlagen');
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Box m="20px">
      <Header
        title="Send Pauses"
        subtitle="adserver_send_pauses — gates für SAM / NL Ad / Push Ad / NL Send Stop"
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#ffffff' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Neue Pause anlegen
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            label="Kunde (z.B. GEV)"
            value={customer}
            onChange={(e) => setCustomer(e.target.value)}
          />
          <TextField
            size="small"
            label="Ansprechpartner"
            value={contactPerson}
            onChange={(e) => setContactPerson(e.target.value)}
          />
          <TextField
            size="small"
            type="date"
            label="Datum"
            InputLabelProps={{ shrink: true }}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <Select
            size="small"
            value={materialType}
            onChange={(e) => setMaterialType(e.target.value as PauseRow['material_type'])}
            sx={{ minWidth: 140 }}
          >
            {MATERIAL_TYPES.map((mt) => (
              <MenuItem key={mt} value={mt}>
                {mt}
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            multiple
            value={clientCodes}
            onChange={(e) => {
              const v = e.target.value;
              setClientCodes(
                (typeof v === 'string' ? v.split(',') : v).filter((c): c is ClientCode =>
                  VALID_CLIENT_CODES.includes(c as ClientCode),
                ),
              );
            }}
            input={<OutlinedInput label="Clients" />}
            renderValue={(selected) => {
              if (selected.length === VALID_CLIENT_CODES.length) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    alle (global)
                  </Typography>
                );
              }
              if (selected.length === 0) {
                return (
                  <Typography variant="body2" color="error">
                    keine — bitte mindestens 1 Client auswählen
                  </Typography>
                );
              }
              return (
                <Stack direction="row" gap={0.5} flexWrap="wrap">
                  {selected.map((c) => (
                    <Chip key={c} size="small" label={c.toUpperCase()} />
                  ))}
                </Stack>
              );
            }}
            sx={{ minWidth: 220 }}
          >
            {VALID_CLIENT_CODES.map((c) => (
              <MenuItem key={c} value={c}>
                <Checkbox checked={clientCodes.includes(c)} size="small" />
                <ListItemText primary={c.toUpperCase()} />
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || clientCodes.length === 0}
          >
            {creating ? 'speichert…' : 'Anlegen'}
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  'Datum',
                  'Material-Type',
                  'Clients',
                  'Kunde',
                  'Ansprechpartner',
                  'Erstellt',
                  '',
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Keine Pausen im Zeitraum (-30 Tage … +60 Tage).
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{formatDate(r.date)}</TableCell>
                  <TableCell>{r.material_type}</TableCell>
                  <TableCell>
                    {!r.client_codes ? (
                      <Typography variant="body2" color="text.secondary">
                        alle (global)
                      </Typography>
                    ) : (
                      <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {r.client_codes.split(',').map((c) => (
                          <Chip key={c} size="small" label={c.toUpperCase()} />
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell>{r.customer || '–'}</TableCell>
                  <TableCell>{r.contact_person || '–'}</TableCell>
                  <TableCell>{r.added_at ? formatDate(r.added_at) : '–'}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleDelete(r.id)} color="error">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AdserverSendPausesPage;
